import { t } from 'i18next';
import { MathUtils, Vector2, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { findLineSegmentIdFromIntersection } from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { findPointIdFromIntersection } from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { MouseInputData } from '../../../../../../utilities/input-event/input-event-data-extractor';
import { ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { initActionInProgress } from '../general';

/**
 * Calculates the rotation for text mode based on the direction from start to end point.
 * Returns 0 if the mouse hasn't moved beyond the threshold.
 * Uses world space coordinates for accurate 3D rotation calculation.
 */
function calculateTextRotation(tool: DesignTool, e?: MouseInputData): number {
  if (!tool.mousePosition || !e || !tool.currentPoint || !tool.actionInProgress?.origin) {
    return 0;
  }

  // Check if mouse has moved beyond the threshold
  if (!tool.mouseMoved(e)) {
    return 0;
  }

  // Get the start and current points, ensuring they're at the same height (2D constraint)
  const startPoint = tool.actionInProgress.origin.clone();
  const currentPoint = tool.currentPoint.clone();
  currentPoint.z = startPoint.z; // Ensure 2D constraint

  // Calculate the direction vector in world space (2D)
  const direction = new Vector2(currentPoint.x - startPoint.x, currentPoint.y - startPoint.y);

  // Calculate the angle in radians (0 = pointing right, π/2 = pointing up)
  const angle = Math.atan2(direction.y, direction.x);

  return angle;
}

/**
 * Calculates the perpendicular line for text sizing.
 * Returns the perpendicular direction vector scaled by screen space movement and the calculated size.
 * All calculations are restricted to 2D space (same height as origin point).
 */
function calculatePerpendicularLine(
  tool: DesignTool,
  e?: MouseInputData,
): { start: Vector3; end: Vector3; size: number } | null {
  if (!tool.mousePosition || !tool.currentPoint || !tool.actionInProgress?.origin || !e) {
    return null;
  }

  // Get the current mouse position from the tool's state
  const startPoint = tool.actionInProgress.origin.clone();
  const currentPoint = tool.currentPoint.clone();

  // Ensure both points are at the same height (2D constraint)
  currentPoint.z = startPoint.z;

  // Calculate the direction vector in 2D space (X, Y only)
  const direction2D = new Vector2(currentPoint.x - startPoint.x, currentPoint.y - startPoint.y);

  // Normalize the 2D direction
  direction2D.normalize();

  // Create perpendicular vector in 2D (rotate 90 degrees: (-y, x))
  const perpendicular2D = new Vector2(-direction2D.y, direction2D.x);

  // Calculate screen space distance for scaling using the same approach as calculateTextRotation
  const screenDirection = new Vector2().subVectors(e.pointer.client, tool.mousePosition);
  const screenDistance = screenDirection.length();

  // Get screen dimensions
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight);

  // Calculate screen space ratio (0.1 to 0.4 of total screen space)
  const screenSpaceRatio = screenDistance / screenDiagonal;

  // Clamp the screen space ratio between 0.1 and 0.4
  const clampedRatio = MathUtils.clamp(screenSpaceRatio, 0.1, 0.4);

  // Map the clamped ratio to a size range (0.5 to 5)
  const minSize = 0.5;
  const maxSize = 5;
  const size = minSize + ((clampedRatio - 0.1) / (0.4 - 0.1)) * (maxSize - minSize);

  // Scale the perpendicular vector by the calculated size
  perpendicular2D.multiplyScalar(size);

  // Create the perpendicular line extending in one direction from the start point
  // Keep the same Z coordinate as the start point (2D constraint)
  const perpendicularStart = startPoint.clone();
  const perpendicularEnd = new Vector3(
    startPoint.x + perpendicular2D.x,
    startPoint.y + perpendicular2D.y,
    startPoint.z,
  );

  return {
    start: perpendicularStart,
    end: perpendicularEnd,
    size,
  };
}

export const createTextAction = (tool: DesignTool): ContextMenuItem => {
  const startPoint = tool.currentPoint?.clone();
  const startPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const startLineId = findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection);

  const disabled = !isDefined(startPoint);

  return {
    title: t('section.tools.textButton.text', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      // Create the perpendicular line picker
      const perpendicularLineCursor = new PickingLine();
      perpendicularLineCursor.updateVertex(0, startPoint);
      perpendicularLineCursor.updateVertex(1, startPoint);

      initActionInProgress(tool, {
        actionName: ActionName.CreateText,
        origin: startPoint,
        originLineSegmentId: startLineId,
        initiate: () => {
          tool.add(perpendicularLineCursor);
          tool.onEdited?.();
        },
        apply: (e?: MouseInputData) => {
          // Calculate the perpendicular line to get the current size
          const perpendicularLine = calculatePerpendicularLine(tool, e);
          const calculatedSize = perpendicularLine?.size ?? 0.5;

          publish(SubscriptionTopic.ShowCreateDesignTextDialog, {
            origin: startPoint,
            startPointId,
            rotation: calculateTextRotation(tool, e),
            text: '',
            size: calculatedSize,
          });
        },
        readyToApply: () => true,
        refresh: () => {
          if (tool.currentPoint) {
            // Ensure the current point is at the same height as the origin (2D constraint)
            const constrainedCurrentPoint = tool.currentPoint.clone();
            constrainedCurrentPoint.z = tool.actionInProgress!.origin!.z;

            tool.cursorLine.visible = true;
            tool.cursorLine.updateVertex(0, tool.actionInProgress!.origin!);
            tool.cursorLine.updateVertex(1, constrainedCurrentPoint);

            // Update the perpendicular line using the last mouse event
            const perpendicularLine = calculatePerpendicularLine(tool, tool.lastMouseEvent);
            if (perpendicularLine) {
              perpendicularLineCursor.updateVertex(0, perpendicularLine.start);
              perpendicularLineCursor.updateVertex(1, perpendicularLine.end);
            }
          }
        },
        cleanup: () => {
          tool.cursorLine.visible = false;
          tool.remove(perpendicularLineCursor);
        },
      });

      tool.contextMenuCallback?.();
    },
    disabled,
  };
};
